import React, { useMemo, useState } from "react";
import { Text } from "components/Typography/text.styled";
import { FontSize } from "config/font";
import { Color } from "config/colors";
import {
  InputContainer,
  InputLabel,
  TextArea,
} from "../textInput/textInput.styled";
import { useTranslation } from "react-i18next";

interface TextAreaWithLabelProps {
  label: string;
  required?: boolean;
  hasError?: boolean;
  fontSize?: FontSize;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  maxLength?: number;
  errorText?: string;
  showCount?: boolean;
  minRows?: number;
  resize?: "none" | "vertical" | "horizontal" | "both";
  marginLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  width?: number;
}

export const TextareaWithLabel: React.FC<TextAreaWithLabelProps> = (props) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = Boolean(props.value && props.value.length > 0);
  const { t } = useTranslation();

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const count = useMemo(() => props.value?.length ?? 0, [props.value]);

  return (
    <InputContainer
      marginLeft={props.marginLeft}
      marginRight={props.marginRight}
      marginTop={props.marginTop}
      marginBottom={props.marginBottom}
      width={props.width}
    >
      <InputLabel
        required={props.required}
        hasValue={hasValue}
        isFocused={isFocused}
      >
        {props.label}
      </InputLabel>

      <TextArea
        value={props.value}
        onChange={props.onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        hasError={props.hasError}
        fontSize={props.fontSize}
        disabled={props.disabled}
        hasValue={hasValue}
        placeholder={props.placeholder}
        maxLength={props.maxLength}
        resize={props.resize}
        minRows={props.minRows}
        width={props.width}
      />

      {props.showCount && typeof props.maxLength === "number" && (
        <Text
          fontSize={FontSize.Tiny}
          color={
            count > (props.maxLength ?? 0) * 0.9 ? Color.Black : Color.Black
          }
          style={{
            marginTop: 6,
            width: 280,
            alignSelf: "flex-end",
            textAlign: "right",
          }}
        >
          {count}/{props.maxLength}
        </Text>
      )}

      {props.errorText && (
        <Text
          fontSize={FontSize.Tiny}
          color={Color.LighterDestructive}
          style={{ marginTop: 6, width: 280 }}
        >
          {props.errorText}
        </Text>
      )}
    </InputContainer>
  );
};
